import CloudSignInModalEmailOnly from '@/components/modal/sign-in-modal/cloud-sign-in-modal-email-only';
import { isKeycloak } from '@/utils/buildProfile';
import dynamic from 'next/dynamic';

import { type SignInModalProps } from './types';

/**
 * Build-profile dispatcher for the email-only sign-in modal.
 *
 * The cloud implementation (`CloudSignInModalEmailOnly`) is Kinde-coupled
 * — its hooks (email submit, social IdPs, redirect-after-auth helpers)
 * only function with the Kinde provider mounted, which isn't the case on
 * on-prem builds. Branching at this top-level boundary keeps the
 * rules-of-hooks lint happy: the cloud path never calls its hooks on
 * on-prem because the cloud component is never rendered.
 *
 * On-prem replaces the modal entirely with a minimal "Sign in" surface
 * (`KeycloakSignInModal`) that hands off to NextAuth's PKCE flow against
 * the local Keycloak realm. Self-registration, password reset, and
 * social IdPs are stripped — admin-provisioned accounts only, per
 * MOD-877's "Email / SMS policy" decision.
 *
 * `KeycloakSignInModal` is lazy-loaded via `next/dynamic({ ssr: false })`
 * because its transitive `next-auth/react` import runs
 * `parseUrl(process.env.NEXTAUTH_URL)` at module top level — cloud
 * builds don't set NEXTAUTH_URL, so a static import here would crash
 * every cloud page render with `TypeError: Invalid URL`. With
 * `ssr: false`, the chunk is excluded from the server bundle and only
 * loaded on the client when the on-prem branch actually renders, which
 * never happens on cloud (`isKeycloak === false`). Same pattern as the
 * `KeycloakAuthShell` lazy-load in `auth-wrapper.tsx` (PR #10925).
 */
const KeycloakSignInModal = dynamic<SignInModalProps>(
  () => import('@/components/modal/sign-in-modal/keycloak-sign-in-modal'),
  { ssr: false }
);

function SignInModalEmailOnly(props: SignInModalProps) {
  if (isKeycloak) return <KeycloakSignInModal {...props} />;
  return <CloudSignInModalEmailOnly {...props} />;
}

export default SignInModalEmailOnly;
